import type {
  SchoolPeriodUserEntity,
  SchoolPeriodUserInputEntity
} from '@/core/infrastructure/schemas/school/user/SchoolPeriodUserSchema'
import {
  SchoolPeriodUserInputSchema,
  SchoolPeriodUserSchema
} from '@/core/infrastructure/schemas/school/user/SchoolPeriodUserSchema'
import { UserRole } from '@/core/domain/resources/enums/UserRole'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator'
import type { AvailableEducationalMemberEntity } from '@/core/infrastructure/schemas/school/user/AvailableEducationalMemberSchema.ts'
import type { AvailableEducationalMember } from '@/core/domain/school/user/AvailableEducationalMember.ts'
import { UserGender } from '@/core/domain/resources/enums/UserGender.ts'

export class SchoolPeriodUser {
  readonly schoolPeriodUserId: number
  readonly userIdentifier: string
  readonly firstname: string
  readonly lastname: string
  readonly username: string
  readonly email?: string
  readonly gender?: UserGender.Values
  readonly birthdate?: string | null
  readonly role: UserRole.Values[]
  readonly createdAt?: string | null
  readonly modifiedAt?: string | null
  hasSignedAgreement?: boolean | null

  constructor(schoolPeriodUser: SchoolPeriodUserEntity) {
    this.schoolPeriodUserId = schoolPeriodUser.schoolPeriodUserId
    this.userIdentifier = schoolPeriodUser.user.identifier
    this.firstname = schoolPeriodUser.user.firstname
    this.lastname = schoolPeriodUser.user.lastname
    this.username = schoolPeriodUser.user.username
    this.email = schoolPeriodUser.user.email
    this.gender = schoolPeriodUser.user.gender
    this.birthdate = schoolPeriodUser.user.birthdate
    this.role = schoolPeriodUser.user.roles
    this.createdAt = schoolPeriodUser.user.createdAt
    this.modifiedAt = schoolPeriodUser.user.modifiedAt
    this.hasSignedAgreement = schoolPeriodUser.hasSignedAgreement
  }

  get fullName() {
    return `${this.firstname} ${this.lastname}`
  }

  get roleLabel(): string {
    const primaryRole = this.role[0]
    switch (primaryRole) {
      case UserRole.Values.STUDENT:
        return 'Élève'
      case UserRole.Values.TEACHER:
        return 'Enseignant'
      case UserRole.Values.ADMIN:
        return 'Administrateur'
      default:
        return primaryRole
    }
  }


  /**
   * Crée une instance de SchoolPeriodUser à partir de données brutes
   */
  public static create(data: unknown): SchoolPeriodUser {
    return EntityValidator.validateAndCreate(
      SchoolPeriodUserSchema,
      data,
      SchoolPeriodUser,
      'SchoolPeriodUser'
    )
  }
}

export class SchoolPeriodUserInput {
  identifier?: string
  firstname: string = ''
  lastname: string = ''
  email?: string = ''
  gender?: UserGender.Values
  roles: UserRole.SchoolValues[] = []
  birthdate?: string | null = null
  schoolPeriodId: number = 0
  hasSignedAgreement?: boolean | null

  constructor(schoolPeriodUserInput?: SchoolPeriodUserInputEntity) {
    if (schoolPeriodUserInput) {
      this.identifier = schoolPeriodUserInput.identifier
      this.firstname = schoolPeriodUserInput.firstname
      this.lastname = schoolPeriodUserInput.lastname
      this.email = schoolPeriodUserInput.email
      this.gender = schoolPeriodUserInput.gender
      this.roles = schoolPeriodUserInput.roles
      this.birthdate = schoolPeriodUserInput.birthdate
      this.schoolPeriodId = schoolPeriodUserInput.schoolPeriodId
      this.hasSignedAgreement = schoolPeriodUserInput.hasSignedAgreement
    }
  }

  get role() {
    return this.roles[0]
  }

  set role(role: UserRole.SchoolValues) {
    this.roles = [role]
    if (role !== UserRole.SchoolValues.STUDENT) {
      this.hasSignedAgreement = undefined
    }
  }

  /**
   * Utilitaire pour créer un SchoolPeriodUser à partir d'un AvailableEducationalMember'
   * @param schoolPeriodId
   * @param member
   */
  public static from(
    schoolPeriodId: number,
    member: AvailableEducationalMemberEntity | AvailableEducationalMember
  ): SchoolPeriodUserInput {
    return SchoolPeriodUserInput.create({ schoolPeriodId, ...member })
  }

  /**
   * Crée une instance de SchoolPeriodUserInput à partir de données brutes
   */
  public static create(data: unknown): SchoolPeriodUserInput {
    return EntityValidator.validateAndCreate(
      SchoolPeriodUserInputSchema,
      data,
      SchoolPeriodUserInput,
      'SchoolPeriodUserInput'
    )
  }
}
